import React, { createContext, HTMLProps, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { SELECTED_FUND_COLORS } from '../../../config/constants';
import { FundsSelectionContext } from '../../../contexts/funds-robot/FundsSelectionContext';
import { Button } from '../../general/Button';
import { IntermediateFundsSelectionContext, DisclaimerSection } from '../funds-selection-screen/FundsSelectionScreen';
import { ComparisonStickyCard, DocumentationLinks } from './comparison-sticky-card/ComparisonStickyCard';
import { StockCompare, TintedStock } from './comparison-stocks/StockCompare';
import { BondCompare, TintedBond } from './comparison-bonds/BondCompare';
import { PortfolioCompare, TintedPortfolio } from './comparison-portfolios/PortfolioCompare';
import { ValueGrowthChart } from './comparison-chart';
import { IntervalPicker } from './comparison-chart';
import { Asset } from '../../../types/Asset';
import { StickyPageSelector } from './comparison-bar-swipable-section/StickyPageSelector';
import { useTranslation } from 'react-i18next';
import { generateLabels as generateChartLabels, getLabelInterval } from '../../../utils/functions';
import { ChartIntervalContext } from '../../../contexts/funds-robot/ChartIntervalContext';
import { FundRatesContext } from '../../../contexts/funds-robot/FundRatesContext';
import { NAVModal } from './comparison-screen-modals/NAVModal';
import { BNLRatesModal } from './comparison-screen-modals/BNLRatesModal';
import { MonthlyReturnsModal } from './comparison-screen-modals/MonthlyReturnsModal';
import { FundsSelectionModal } from './comparison-screen-modals/FundsSelectionModal';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';

export type SwipeOffsetFn = (offset: { x: number; y: number }) => void;

//Probably needs to be specified somewhere else but not sure where exactly
export const showBNLRatesFundIDs = ['8003'];

export const ComparisonScreenContext = createContext({ isHeaderVisible: true, setIsHeaderVisible: (a: boolean) => {} });

export const ComparisonScreenFundSelectionContext = createContext<{
    selectedFund: Asset | null;
    setSelectedFund: (fund: Asset) => void;
    selectedFundIndex: number;
    onSwipe: SwipeOffsetFn;
}>({
    selectedFund: undefined!,
    setSelectedFund: undefined!,
    selectedFundIndex: undefined!,
    onSwipe: undefined!,
});

export const ComparisonScreen: React.FC<{} & HTMLProps<HTMLDivElement>> = ({ className }) => {
    const history = useHistory();
    const { t } = useTranslation('funds');
    const fundsSelectionContext = useContext(FundsSelectionContext);
    const chartIntervalContext = useContext(ChartIntervalContext);
    const fundRatesContext = useContext(FundRatesContext);

    const {
        funds: {
            removeAll: removeAllIntermediateFunds,
            set: setIntermediateFunds,
            selected: selectedIntermediaryFunds,
        },
    } = useContext(IntermediateFundsSelectionContext);
    const [isSelectionModalShown, setIsSelectionModalShown] = useState(false);
    const [isMonthylyReturnModalShown, setIsMonthylyReturnModalShown] = useState(false);
    const [isNAVModalShown, setIsNAVModalShown] = useState(false);
    const [isBNLRatesModalShown, setIsBNLRatesModalShown] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(fundsSelectionContext.funds.selected[0]?.id);
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);

    const selectedAsset = fundsSelectionContext.funds.selected.find((f) => f.id === selectedAssetId);
    // This reduce is used to determine whether the total selection is changed.
    // Reducing the total selection to a primitive type makes the total selection comparable.
    const totalSelectionKey = fundsSelectionContext.funds.selected.reduce((total, funds) => {
        return total + funds.id + '';
    }, '');

    useEffect(() => {
        if (fundsSelectionContext.funds.selected.filter((funds) => funds.id === selectedAsset?.id).length === 0) {
            // The selected fund does not exist in the total selection of funds, so set selected fund to first of total selection
            setSelectedAssetId(fundsSelectionContext.funds.selected[0]?.id);
        }
    }, [totalSelectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const chartData = fundsSelectionContext.funds.selected.map((f) => {
        return {
            id: f.id,
            label: f.name,
            data: fundRatesContext.rates.get(f.id)?.withinInterval.indexed || [],
        };
    });

    const chartLabels = useMemo(() => {
        if (chartData.length === 0) return [];
        const labelInterval = getLabelInterval(chartIntervalContext.selected.interval, chartData[0].data);
        return generateChartLabels(labelInterval);
    }, [chartIntervalContext.selected.interval]); // eslint-disable-line react-hooks/exhaustive-deps

    const stocks = fundsSelectionContext.funds.selected
        .filter((f) => f.category === 'Stock')
        .map((fund, i) => {
            return {
                ...fund,
                tint: SELECTED_FUND_COLORS[i],
            } as TintedStock;
        });

    const bonds = fundsSelectionContext.funds.selected
        .filter((f) => f.category === 'Bond')
        .map((fund, i) => {
            return {
                ...fund,
                tint: SELECTED_FUND_COLORS[i],
            } as TintedBond;
        });
    const portfolios = fundsSelectionContext.funds.selected
        .filter((f) => f.category === 'Portfolio')
        .map((fund, i) => {
            return {
                ...fund,
                tint: SELECTED_FUND_COLORS[i],
            } as TintedPortfolio;
        });

    const propagateIntermediateSelection = React.useCallback(() => {
        // Every time the comparison screen is shown, set the intermediate selection to the "real" selection
        fundsSelectionContext.funds.set(selectedIntermediaryFunds);
    }, [fundsSelectionContext.funds.set, selectedIntermediaryFunds]); // eslint-disable-line react-hooks/exhaustive-deps

    // Intermediate selections are propagated every time the selection modal state changes and on every mount.
    useEffect(() => {
        if (selectedIntermediaryFunds.length === 0) return;
        propagateIntermediateSelection();
    }, [isSelectionModalShown]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return history.listen((location, action) => {
            if (action === 'PUSH' && location.pathname === '/comparison') {
                setIsSelectionModalShown(false);
                setIsMonthylyReturnModalShown(false);
                setIsNAVModalShown(false);
                setIsBNLRatesModalShown(false);
            }
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedFundIndex = fundsSelectionContext.funds.selected.findIndex((f) => {
        return f.id === selectedAsset?.id;
    });

    return (
        <ComparisonScreenFundSelectionContext.Provider
            value={{
                selectedFund: selectedAsset || null,
                setSelectedFund: (fund) => {
                    setSelectedAssetId(fund.id);
                },
                selectedFundIndex,
                onSwipe: (offset) => {
                    if (offset.x < -20) {
                        if (selectedFundIndex > 0) {
                            setSelectedAssetId(fundsSelectionContext.funds.selected[selectedFundIndex - 1].id);
                        }
                        // To the left
                    } else if (offset.x > 20) {
                        // To the right
                        if (selectedFundIndex < fundsSelectionContext.funds.selected.length - 1) {
                            setSelectedAssetId(fundsSelectionContext.funds.selected[selectedFundIndex + 1].id);
                        }
                    }
                },
            }}
        >
            <ComparisonScreenContext.Provider value={{ isHeaderVisible, setIsHeaderVisible }}>
                <FundsSelectionModal
                    isShown={isSelectionModalShown}
                    onClose={() => {
                        setIsSelectionModalShown(false);
                        history.push('/comparison');
                    }}
                />
                {selectedAsset?.category === 'Portfolio' && (
                    <MonthlyReturnsModal
                        portfolio={selectedAsset}
                        isShown={isMonthylyReturnModalShown}
                        onClose={() => {
                            setIsMonthylyReturnModalShown(false);
                        }}
                    />
                )}
                {(selectedAsset?.category === 'Stock' || selectedAsset?.category === 'Bond') && (
                    <NAVModal
                        fund={selectedAsset}
                        isShown={isNAVModalShown}
                        onClose={() => {
                            setIsNAVModalShown(false);
                        }}
                    />
                )}
                {(selectedAsset?.category === 'Stock' || selectedAsset?.category === 'Bond') && (
                    <BNLRatesModal
                        fund={selectedAsset}
                        isShown={isBNLRatesModalShown}
                        onClose={() => {
                            setIsBNLRatesModalShown(false);
                        }}
                    />
                )}
                <div id="comparison-screen" className={`flex items-start ${className}`}>
                    <ComparisonStickyCard className="hidden xl:block">
                        <div className="mt-12">
                            {fundsSelectionContext.selectionType === 'Portfolio' && (
                                <Button
                                    id="show-monthly-returns"
                                    buttonType="secondary"
                                    className={`mt-4 `}
                                    onClick={() => {
                                        setIsMonthylyReturnModalShown(true);
                                    }}
                                >
                                    {t('funds-comparison-screen-monthly-return')}
                                </Button>
                            )}
                            {fundsSelectionContext.selectionType !== 'Portfolio' && (
                                <Button
                                    id="show-daily-rates"
                                    buttonType="secondary"
                                    className={`mt-4`}
                                    onClick={() => {
                                        showBNLRatesFundIDs.indexOf(selectedAsset?.id + '') > -1
                                            ? setIsBNLRatesModalShown(true)
                                            : setIsNAVModalShown(true);
                                    }}
                                >
                                    {t('funds-comparison-screen-inspect-rates')}
                                </Button>
                            )}
                        </div>
                    </ComparisonStickyCard>
                    <div className="flex-1 min-w-0 px-2 lg:px-0 xl:pl-8">
                        <div className="flex flex-row justify-between items-baseline">
                            <div className="flex flex-row">
                                <IntervalPicker
                                    interval={chartIntervalContext.selected.type}
                                    onIntervalChange={(interval, custom) => {
                                        chartIntervalContext.select(interval, custom);
                                    }}
                                />
                                <Tooltip
                                    place="bottom"
                                    className="md:ml-8"
                                    tooltipTitle={
                                        fundsSelectionContext.selectionType === 'Portfolio'
                                            ? t('tooltip-graph-portfolio-title')
                                            : t('tooltip-graph-bond-stock-title')
                                    }
                                    tooltipText={
                                        fundsSelectionContext.selectionType === 'Portfolio'
                                            ? t('tooltip-graph-portfolio-text')
                                            : t('tooltip-graph-bond-stock-text')
                                    }
                                >
                                    <TooltipIndicator>?</TooltipIndicator>
                                </Tooltip>
                            </div>
                            <div className="hidden md:flex flex-row gap-2">
                                <Button
                                    id="back-to-overview"
                                    buttonType="secondary"
                                    className=""
                                    onClick={() => {
                                        removeAllIntermediateFunds();
                                        setIsSelectionModalShown(true);
                                    }}
                                >
                                    {t('funds-comparison-screen-back-to-overview')}
                                </Button>
                                <Button
                                    id="change-comparison"
                                    buttonType="primary"
                                    className=""
                                    onClick={() => {
                                        setIntermediateFunds(fundsSelectionContext.funds.selected);
                                        setIsSelectionModalShown(true);
                                    }}
                                >
                                    {fundsSelectionContext.funds.selected.length > 1
                                        ? t('funds-comparison-screen-change-comparison')
                                        : t('funds-comparison-screen-create-comparison')}
                                </Button>
                            </div>
                        </div>
                        <div className={`flex items-center justify-center mt-8`}>
                            <ValueGrowthChart
                                isLoading={fundRatesContext.isLoading}
                                labels={chartLabels}
                                data={chartData}
                            />
                        </div>
                        <div className="flex md:hidden flex-col sm:flex-row justify-around">
                            <Button
                                id="back-to-overview"
                                buttonType="secondary"
                                className="mt-4 mx-8"
                                onClick={() => {
                                    removeAllIntermediateFunds();
                                    setIsSelectionModalShown(true);
                                }}
                            >
                                {t('funds-comparison-screen-back-to-overview')}
                            </Button>
                            <Button
                                id="change-comparison"
                                buttonType="primary"
                                className="mt-4 mx-8"
                                onClick={() => {
                                    setIsSelectionModalShown(true);
                                }}
                            >
                                {t('funds-comparison-screen-change-comparison')}
                            </Button>
                        </div>
                        {fundsSelectionContext.selectionType === 'Stock' && (
                            <StockCompare
                                className={`mt-8 ${
                                    fundsSelectionContext.funds.selected.length === 3 ? '' : 'md:w-2/3'
                                }`}
                                startDate="01/01/2020"
                                stocks={stocks}
                            />
                        )}
                        {fundsSelectionContext.selectionType === 'Bond' && (
                            <BondCompare
                                className={`mt-8 ${
                                    fundsSelectionContext.funds.selected.length === 3 ? '' : 'md:w-2/3'
                                }`}
                                startDate="01/01/2020"
                                bonds={bonds}
                            />
                        )}
                        {fundsSelectionContext.selectionType === 'Portfolio' && (
                            <PortfolioCompare
                                className={`mt-8 ${
                                    fundsSelectionContext.funds.selected.length === 3 ? '' : 'md:w-2/3'
                                }`}
                                startDate="01/01/2020"
                                portfolios={portfolios}
                            />
                        )}
                        <DisclaimerSection onFundDetailsScreen={true} />
                        <StickyColumnNamer
                            assets={
                                fundsSelectionContext.selectionType === 'Portfolio'
                                    ? portfolios
                                    : fundsSelectionContext.selectionType === 'Stock'
                                    ? stocks
                                    : bonds
                            }
                        />
                    </div>
                </div>
                {selectedAsset && <DocumentationLinks asset={selectedAsset} className="mx-4 mt-12 xl:hidden" />}
                <div className="flex flex-col xl:hidden px-4 mt-12">
                    {fundsSelectionContext.selectionType === 'Portfolio' && (
                        <Button
                            id="show-monthly-returns"
                            buttonType="secondary"
                            className={`block xl:hidden`}
                            onClick={() => {
                                setIsMonthylyReturnModalShown(true);
                            }}
                        >
                            {t('funds-comparison-screen-monthly-return')}
                        </Button>
                    )}
                    {fundsSelectionContext.selectionType !== 'Portfolio' && (
                        <Button
                            id="show-daily-rates"
                            buttonType="secondary"
                            className={`block xl:hidden`}
                            onClick={() => {
                                showBNLRatesFundIDs.indexOf(selectedAsset?.id + '') > -1
                                    ? setIsBNLRatesModalShown(true)
                                    : setIsNAVModalShown(true);
                            }}
                        >
                            {t('funds-comparison-screen-inspect-rates')}
                        </Button>
                    )}
                </div>
                <StickyPageSelector className="mt-4 md:mt-8" />
            </ComparisonScreenContext.Provider>
        </ComparisonScreenFundSelectionContext.Provider>
    );
};

export const StickyColumnNamer: React.FC<
    { assets: TintedStock[] | TintedBond[] | TintedPortfolio[] } & HTMLProps<HTMLDivElement>
> = ({ assets, className, ...rest }) => {
    const comparisonScreenContext = useContext(ComparisonScreenContext);

    return (
        <div
            className={`hidden sticky bottom-8 left-0 lg:flex flex-row flex-1 min-w-0 mt-16 lg:px-0 bg-bnd-black-70 text-white shadow-lg rounded-lg ${className} ${
                comparisonScreenContext.isHeaderVisible ? 'opacity-0' : 'opacity-100'
            } transition-opacity`}
            {...rest}
        >
            {assets.map((asset: TintedStock | TintedPortfolio | TintedBond, index: number) => {
                return (
                    <div
                        key={asset.id}
                        className={`flex-1 flex flex-row items-baseline px-8 py-4 ${
                            index !== 2 && 'border-r-2 border-gray-300'
                        }`}
                    >
                        <div
                            className="mr-3 flex items-center justify-center"
                            style={{ height: 'calc(1.5 * var(--srem))' }}
                        >
                            <div className="rounded-full w-4 h-4" style={{ backgroundColor: asset.tint }}></div>
                        </div>
                        {asset.name}
                    </div>
                );
            })}
        </div>
    );
};
